/**
 * Wraps a Promise with a timeout to prevent indefinite hanging.
 *
 * If the promise doesn't resolve/reject within the specified timeout,
 * it will be rejected with a timeout error.
 *
 * @template T - The type of the promise result
 * @param {Promise<T>} promise - The promise to wrap with timeout
 * @param {number} timeout - Timeout in milliseconds (default: 30000ms = 30s)
 * @returns {Promise<T>} The original promise result or timeout error
 *
 * @throws {Error} Throws "Request timeout" error if timeout is reached
 *
 * @example
 * ```typescript
 * // Wrap a Supabase query with 30s timeout
 * const { data, error } = await fetchWithTimeout(
 *   supabase.from("loyalty_cards").select("*"),
 *   30000
 * );
 * ```
 *
 * @example
 * ```typescript
 * // Use default 30s timeout
 * const result = await fetchWithTimeout(
 *   fetch('https://api.example.com/data')
 * );
 * ```
 */
export async function fetchWithTimeout<T>(
  promise: Promise<T>,
  timeout: number = 30000
): Promise<T> {
  // Create a promise that rejects after timeout
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error('Request timeout - La requête a pris trop de temps'));
    }, timeout);
  });

  // Race between the actual promise and the timeout
  // Whichever resolves/rejects first wins
  return Promise.race([promise, timeoutPromise]);
}

/**
 * Detects if an error is a timeout error.
 *
 * @param {any} error - The error to check
 * @returns {boolean} True if the error is a timeout error
 *
 * @example
 * ```typescript
 * try {
 *   await fetchWithTimeout(somePromise);
 * } catch (error) {
 *   if (isTimeoutError(error)) {
 *     console.log("Request timed out");
 *   }
 * }
 * ```
 */
export function isTimeoutError(error: any): boolean {
  return error?.message?.includes('timeout') || error?.message?.includes('timed out');
}
